// Intención de checkout (flujo B: registro/login PRIMERO
// → luego checkout). Cuando alguien llega a /login con ?plan=X&interval=Y guardamos
// la intención aquí; tras autenticarse (registro nuevo, login o email confirmado) un
// efecto global la recoge, invoca create-checkout-session con el JWT de la sesión y
// redirige a Stripe. Persistimos en localStorage para sobrevivir al ciclo de auth
// (incluida la confirmación de email, que vuelve por /auth/callback en otra carga).

import { supabase } from '@/integrations/supabase/client';

export type CheckoutPlan = 'indie' | 'line-producer' | 'productora' | 'estudio';
export type CheckoutInterval = 'month' | 'year';

export interface PendingCheckout {
  plan: CheckoutPlan;
  interval: CheckoutInterval;
}

const KEY = 'pelip:pendingCheckout';
const VALID_PLANS: readonly CheckoutPlan[] = ['indie', 'line-producer', 'productora', 'estudio'];

// Evita relanzar el checkout dos veces en la misma carga (p.ej. si el estado de auth
// se estabiliza en varios pasos). Se resetea al recargar la página.
let launching = false;

export function normalizePlan(raw: string | null | undefined): CheckoutPlan | null {
  const v = String(raw || '').toLowerCase();
  return (VALID_PLANS as readonly string[]).includes(v) ? (v as CheckoutPlan) : null;
}

export function normalizeInterval(raw: string | null | undefined): CheckoutInterval {
  return String(raw || '').toLowerCase() === 'year' ? 'year' : 'month';
}

export function setPendingCheckout(pc: PendingCheckout): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(pc));
  } catch {
    // localStorage no disponible: se ignora (el checkout directo sigue funcionando).
  }
}

export function getPendingCheckout(): PendingCheckout | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingCheckout>;
    const plan = normalizePlan(parsed.plan);
    if (!plan) return null;
    return { plan, interval: normalizeInterval(parsed.interval) };
  } catch {
    return null;
  }
}

export function clearPendingCheckout(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // no-op
  }
}

// Invoca create-checkout-session (adjunta el JWT de la sesión automáticamente) y
// redirige a Stripe. Limpia la intención ANTES de redirigir para que un cancel/back
// desde Stripe no vuelva a lanzar el checkout. Devuelve false si no pudo iniciar.
export async function launchCheckout(plan: CheckoutPlan, interval: CheckoutInterval): Promise<boolean> {
  if (launching) return true;
  launching = true;
  // Limpiamos ya: si algo falla, mostramos error y NO reintentamos en bucle.
  clearPendingCheckout();
  try {
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: { plan, interval },
    });
    // I1 — la org YA tiene un plan de pago activo: el backend no crea un 2º checkout
    // (evita doble suscripción/doble cobro). En vez de ir a Stripe, abrimos el Customer
    // Portal para gestionar la suscripción existente; si no hay portal, a /plan con aviso.
    if ((data as { already_subscribed?: boolean })?.already_subscribed) {
      const { data: portal } = await supabase.functions.invoke('create-portal-session');
      const portalUrl = (portal as { url?: string })?.url;
      window.location.href = portalUrl || '/plan?already_subscribed=1';
      return true;
    }
    const url = (data as { url?: string })?.url;
    if (error || !url) {
      launching = false;
      return false;
    }
    window.location.href = url;
    return true;
  } catch {
    launching = false;
    return false;
  }
}
